import { useLingui } from "@lingui/react/macro";
import { useEffect, useRef, useState } from "react";

import { usePoke } from "~/hook/domain/usePoke.ts";
import { useUser } from "~/ui/provider/Auth.tsx";
import { useNotification } from "~/ui/provider/Notification.tsx";

const MAX_RECORD_SECONDS = 3.0;

export const usePokeWithAudio = (email: string, close: () => void) => {
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [micPermissionError, setMicPermissionError] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<null | ReturnType<typeof setInterval>>(null);
  const testAudioRef = useRef<HTMLAudioElement | null>(null);
  const startTimeRef = useRef<number>(0);

  const { isMutating, trigger } = usePoke();
  const { client } = useUser();
  const push = useNotification();
  const { t } = useLingui();

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (testAudioRef.current) {
        testAudioRef.current.pause();
      }
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== "inactive"
      ) {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  const stopRecording = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }
    if (startTimeRef.current > 0) {
      setDuration(
        Math.min(
          MAX_RECORD_SECONDS,
          (Date.now() - startTimeRef.current) / 1000,
        ),
      );
    }
  };

  const startRecording = async () => {
    setMicPermissionError(false);
    audioChunksRef.current = [];
    setAudioBlob(null);
    setDuration(0);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });

      let options = { mimeType: "audio/webm" };
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options = { mimeType: "audio/mp4" };
      }
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options = { mimeType: "" };
      }

      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const mimeType = mediaRecorder.mimeType || "audio/webm";
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        setAudioBlob(blob);
        setRecording(false);

        stream.getTracks().forEach((track) => {
          track.stop();
        });
      };

      startTimeRef.current = Date.now();
      mediaRecorder.start();
      setRecording(true);

      timerRef.current = setInterval(() => {
        const currentDuration = (Date.now() - startTimeRef.current) / 1000;
        if (currentDuration >= MAX_RECORD_SECONDS) {
          stopRecording();
        } else {
          setDuration(currentDuration);
        }
      }, 100);
    } catch (err) {
      console.error("Failed to access microphone:", err);
      setMicPermissionError(true);
      push({ content: t`마이크 접근 권한이 필요합니다.` });
    }
  };

  const handleToggleRecord = () => {
    if (recording) {
      stopRecording();
    } else {
      void startRecording();
    }
  };

  const handlePlayTest = () => {
    if (!audioBlob) return;

    if (playing && testAudioRef.current) {
      testAudioRef.current.pause();
      setPlaying(false);
      return;
    }

    if (testAudioRef.current) {
      testAudioRef.current.pause();
    }

    const audioUrl = URL.createObjectURL(audioBlob);
    const audio = new Audio(audioUrl);
    testAudioRef.current = audio;

    audio.onended = () => {
      setPlaying(false);
    };

    void audio.play().then(() => {
      setPlaying(true);
    });
  };

  const handleRetake = () => {
    if (testAudioRef.current) {
      testAudioRef.current.pause();
    }
    setAudioBlob(null);
    setDuration(0);
    setPlaying(false);
  };

  const handleSend = async () => {
    if (!audioBlob) return;
    setIsUploading(true);
    try {
      const formData = new FormData();
      const ext = audioBlob.type.includes("mp4") ? "m4a" : "webm";
      formData.append("audio", audioBlob, `audio.${ext}`);

      const audioUrl = await client
        .post("util/audio", { body: formData })
        .text();

      await trigger({
        email,
        payload: { audioUrl, duration, type: "audio" },
      });
      close();
    } catch {
      push({ content: t`다시 시도해주세요.` });
    } finally {
      setIsUploading(false);
    }
  };

  const disabled = isMutating || isUploading;
  const formattedDuration = duration.toFixed(1);

  return {
    audioBlob,
    disabled,
    formattedDuration,
    handlePlayTest,
    handleRetake,
    handleSend,
    handleToggleRecord,
    micPermissionError,
    playing,
    recording,
  };
};
